/* @refresh reset */

import type { CSSProperties } from 'react';

import { useCallback, useMemo, useRef, type RefObject } from 'react';

import { Loader } from '../../primitives/Loader.js';

import { Paper } from '../../primitives/Paper.js';

import { ScrollArea } from '../../primitives/ScrollArea.js';

import { Text } from '../../primitives/Text.js';
import { cx } from '../../utils/cx.js';
import { DataTableEmptyState } from './DataTableEmptyState.js';
import { useMediaQuery } from '../../utils/useMediaQuery.js';

import { useDataTableRowHorizontalScroll } from '../useDataTableRowHorizontalScroll.js';


import type { DataTableDensity } from './dataTableCell.js';

import {
  hasStickyHorizontalScroll,
  resolveColumnsWithPin,
  resolveEffectivePinStartColumnKey,
} from './dataTableColumns.js';

import { DataTablePaginationBar } from './DataTablePagination.js';

import { DataTableStackedView } from './DataTableStackedView.js';

import { DataTableTableView } from './DataTableTableView.js';
import { useDataTableReceive } from './useDataTableReceive.js';

import { resolveDataTableLayout } from './resolveDataTableLayout.js';

import { getDataTableRootProps, getDataTableShellProps } from './useDataTableChrome.js';

import { buildWpColumnPinning, useDataTableCore } from './useDataTableCore.js';

import { defaultListHeightCap, useDataTablePagination } from './useDataTablePagination.js';
import {
  shouldVirtualizeDataTableRows,
  useDataTableScrollParentReady,
  useDataTableViewportLockChain,
  type DataTableVirtualizeRows,
} from './useDataTableRowVirtualizer.js';

import { mergeDataTableVariantProps } from './dataTableVariant.js';

import type { DataTableProps } from './types.js';
import {
  getPageSelectionState,
  togglePageSelection,
  countSelectedOnPage,
} from './useDataTableSelection.js';

import { DataTableSelectionBar } from './DataTableSelectionBar.js';

import styles from './DataTable.module.css';

import './DataTableColumnResize.css';



export type {

  Column,

  DataTableProps,

  DataTablePagination,

  DataTableClientPagination,

  DataTableServerPagination,

  SortDirection,

  DataTableLayoutKind,

  DataTableSelection,

} from './types.js';

export type { DataTableVariant } from './dataTableVariant.js';

export type { DataTableColumnWidthMap } from './columnWidth.js';

export { DEFAULT_CLIENT_PAGE_SIZE, DATATABLE_PAGINATED_HEIGHT_CAP, VIRTUALIZE_ROW_THRESHOLD } from './constants.js';



export {

  RowMenu,

  RowMenuDivider,

  RowMenuItem,

  DataTableRowActionsContent,

  type RowMenuItemProps,

} from './DataTableRowActions.js';

export { DataTableSortableTr } from './DataTableSortableTr.js';

export { DataTableColumnHeader } from './DataTableHeader.js';

export { DataTableSelectionBar } from './DataTableSelectionBar.js';
export type {
  DataTableSelectionBarProps,
  DataTableSelectionBarSelectAll,
} from './DataTableSelectionBar.js';
export {
  DataTableSelectionDockAction,
} from './DataTableSelectionDockAction.js';
export type { DataTableSelectionDockActionProps } from './DataTableSelectionDockAction.js';
export {
  useDataTableSelection,
  getPageSelectionState,
  togglePageSelection,
  countSelectedOnPage,
} from './useDataTableSelection.js';
export type { PageSelectionState } from './useDataTableSelection.js';


export function DataTable<T>(propsIn: DataTableProps<T>) {

  const merged = mergeDataTableVariantProps(propsIn.variant, propsIn);

  const {

    variant = 'list',

  columns,

  data,

  rowKey,

  loading,

  error,

  emptyMessage = 'Aucune donnée.',
  emptyDescription,
  emptyAction,
  emptyVariant = 'default',
  compactEmpty = false,

  quickCreateCells,

  quickCreateCollapsed,

  quickCreateExpanded,

  responsiveStacked,

  onRowClick,

  rowProps,

  rowLeadingActions,

  rowActions,

  actionsHeaderFilter,
  actionsHeaderFilterActive,
  actionsHeaderFilterBadge,
  onActionsHeaderFilterReset,
  actionsHeaderFilterResetAriaLabel,
  actionsHeaderAriaLabel,

  selection,

  selectionBar,

    pagination,

  maxBodyHeight,

  fixedLayout = false,

  compactActions = false,

    density: densityProp,

    classNames,

  noHorizontalScroll = false,

  pinStartColumnKey,

  scrollable = true,

    rowTrWrapper,

    virtualizeRows,

  } = merged;



  const density: DataTableDensity = densityProp ?? (compactActions ? 'compact' : 'default');

  const narrow = useMediaQuery('(max-width: 47.99em)');

  const mobileTableScroll = narrow && !noHorizontalScroll;

  const pinEndColumns = !narrow;

  const effectivePinStartColumnKey = useMemo(
    () => resolveEffectivePinStartColumnKey(columns, pinStartColumnKey, mobileTableScroll),
    [columns, pinStartColumnKey, mobileTableScroll],
  );

  const resolvedColumns = useMemo(
    () => resolveColumnsWithPin(columns, effectivePinStartColumnKey, pinEndColumns),
    [columns, effectivePinStartColumnKey, pinEndColumns],
  );

  const hasActionColumn = Boolean(rowLeadingActions || rowActions);
  const hasSelectionColumn = Boolean(selection);

  const stickyHScroll = hasStickyHorizontalScroll(
    resolvedColumns,
    hasActionColumn,
    noHorizontalScroll,
    mobileTableScroll,
  );

  const pinActionsEnd = stickyHScroll && hasActionColumn && pinEndColumns;
  const pinDeclarativeActEnd =
    pinEndColumns && resolvedColumns.some((c) => c.key === 'act' && c.sticky === 'end');
  const stickyEndFlush = pinActionsEnd || pinDeclarativeActEnd;
  const stickyActionsEnd = pinActionsEnd;

  const totalCols = resolvedColumns.length + (hasActionColumn ? 1 : 0);



  const hasAnyFilter =
    resolvedColumns.some((c) => c.headerFilter != null) || Boolean(actionsHeaderFilter);

  const effectiveFixedLayout = (fixedLayout || hasAnyFilter) && !mobileTableScroll;



  const showQuickCreateRow =

    Boolean(quickCreateCells?.length) &&

    (!quickCreateCollapsed || quickCreateExpanded === true || quickCreateExpanded === false);

  const stackedLayout = Boolean(responsiveStacked && narrow && !showQuickCreateRow);



  /** Column drag-resize disabled until resize/pin bugs are fixed. */

  const columnResizeEnabled = false;



  const tableRef = useRef<HTMLTableElement>(null);

  const layoutKind = resolveDataTableLayout({ variant, pagination });



  const layout = useDataTablePagination({

    layout: layoutKind,

    data,

    pagination,

    heightCap: defaultListHeightCap(maxBodyHeight),

    maxBodyHeight,

    tableRef,

    density,

    stackedLayout,

    showQuickCreateRow,

    hasFilterHeader: hasAnyFilter,

  });

  const pageSelectableKeys = useMemo(() => {
    if (!selection) return [] as string[];
    return layout.rowSource
      .filter((row) => selection.isRowSelectable?.(row) !== false)
      .map((row) => rowKey(row));
  }, [selection, layout.rowSource, rowKey]);

  const receiveRowKeys = useMemo(
    () => layout.rowSource.map((row) => rowKey(row)),
    [layout.rowSource, rowKey],
  );
  const { animateReceive } = useDataTableReceive(loading, receiveRowKeys);
  const receiving = Boolean(loading);

  const pageSelectionState = useMemo(
    () =>
      selection
        ? getPageSelectionState(selection.selectedKeys, pageSelectableKeys)
        : ('none' as const),
    [selection, pageSelectableKeys],
  );

  const onTogglePageSelection = useCallback(() => {
    if (!selection || pageSelectableKeys.length === 0) return;
    selection.onSelectionChange(
      togglePageSelection(selection.selectedKeys, pageSelectableKeys),
    );
  }, [selection, pageSelectableKeys]);

  const onToggleRowSelection = useCallback(
    (key: string) => {
      if (!selection) return;
      const next = new Set(selection.selectedKeys);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      selection.onSelectionChange(next);
    },
    [selection],
  );

  const selectionActive = (selection?.selectedKeys.size ?? 0) > 0;

  const onClearSelection = useCallback(() => {
    selection?.onSelectionChange(new Set());
  }, [selection]);

  const pageSelectedCount = useMemo(
    () => (selection ? countSelectedOnPage(selection.selectedKeys, pageSelectableKeys) : 0),
    [selection, pageSelectableKeys],
  );

  const selectionDock = selection ? (
    <DataTableSelectionBar
      variant="dock"
      count={selection.selectedKeys.size}
      pageCount={selectionBar?.pageCount ?? pageSelectedCount}
      onClear={onClearSelection}
      countLabel={selectionBar?.countLabel}
      clearLabel={selectionBar?.clearLabel}
      selectAll={selectionBar?.selectAll}
    >
      {selectionBar?.actions}
    </DataTableSelectionBar>
  ) : null;

  const scrollParentRef = useRef<HTMLDivElement>(null);
  const hasScrollParent = layout.showFooter || scrollable;
  const virtualizeRowsEnabled = shouldVirtualizeDataTableRows({
    virtualizeRows: virtualizeRows as DataTableVirtualizeRows | undefined,
    rowCount: layout.rowSource.length,
    stackedLayout,
    hasRowTrWrapper: Boolean(rowTrWrapper),
    hasScrollParent,
  });
  const scrollParentReady = useDataTableScrollParentReady(
    scrollParentRef,
    virtualizeRowsEnabled && hasScrollParent,
  );
  const viewportLockChain = useDataTableViewportLockChain(
    scrollParentRef,
    layout.showFooter && layout.fitViewport,
  );
  const fitViewportActive = layout.fitViewport && viewportLockChain;
  const virtualizeRowsActive = virtualizeRowsEnabled && scrollParentReady;
  const assignTableBodyRegionRef = (el: HTMLDivElement | null) => {
    layout.tableBodyRegionRef.current = el;
    if (layout.showFooter) scrollParentRef.current = el;
  };



  const rowHScrollDrag = stickyHScroll && narrow && !stackedLayout;

  const { suppressRowClickRef, tbodyProps: rowHScrollTbodyProps } = useDataTableRowHorizontalScroll(

    layout.tableBodyRegionRef,

    rowHScrollDrag,

  );



  const columnPinning = useMemo(

    () =>

      buildWpColumnPinning(resolvedColumns, {

        enabled: stickyHScroll,

        pinActionsEnd,

      }),

    [resolvedColumns, stickyHScroll, pinActionsEnd],

  );



  const {

    table: wpTable,

    resizingKey,

    colGroupCols,

    tableWidthPx: resizableTableWidthPx,

    mergeCellStyles,

    onResizePointerDown,

    resetColumnWidth,

    actionColumnWidthPx,

  } = useDataTableCore({

    data: layout.rowSource,

    columns: resolvedColumns,

    rowKey,

    hasActionColumn,

    columnResizeEnabled,

    columnPinning,

    enableColumnPinning: stickyHScroll,

    horizontalScrollLayout: mobileTableScroll,

    stickyEndInsetPx: stickyEndFlush ? 0 : 12,

    compactActions,

    hasRowLeadingActions: Boolean(rowLeadingActions),

    hasRowActionsMenu: Boolean(rowActions),

    tableRef,

  });



  const resizableTableStyle: CSSProperties | undefined = columnResizeEnabled

    ? {

          width: '100%',

        ...(resizableTableWidthPx != null && resizableTableWidthPx > 0

          ? { minWidth: `${resizableTableWidthPx}px` }

          : {}),

      }

    : stickyHScroll

      ? { width: '100%' }

    : undefined;



  const paginationFooter = layout.showFooter ? (
      <div ref={layout.paginationFooterRef} className={styles.paginationSlot}>

        <DataTablePaginationBar

          total={layout.total}

          pageIndex={layout.safePage}

          pageCount={layout.pageCount}

          onPrev={layout.onPrev}

          onNext={layout.onNext}

        />

      </div>

    ) : null;



  if (error) {

    return (

      <Text size="sm" c="error">

        {error}

      </Text>

    );

  }



  if (stackedLayout) {

    const stackedInner = (

      <DataTableStackedView

        columns={resolvedColumns}

        rowSource={layout.rowSource}

        dataLength={data.length}

        emptyMessage={emptyMessage}
        emptyDescription={emptyDescription}
        emptyAction={emptyAction}
        emptyVariant={emptyVariant}

        rowKey={rowKey}

        hasActionColumn={hasActionColumn}

        selection={selection}

        selectionActive={selectionActive}

        onToggleRowSelection={onToggleRowSelection}

        rowLeadingActions={rowLeadingActions}

        rowActions={rowActions}

        onRowClick={onRowClick}

        rowProps={rowProps}
        receiving={receiving}
        animateReceive={animateReceive}

      />

    );



    if (!layout.showFooter) {

      return (

        <Paper withBorder p="none" radius="md" shadow="none" style={{ boxShadow: 'none' }}>

          {stackedInner}

        </Paper>

      );

    }



    return (

      <Paper
        withBorder
        p="none"
        radius="md"
        shadow="none"
        style={{ boxShadow: 'none', overflow: 'hidden', ...layout.shellPaperStyle }}
      >

        <div ref={layout.paginatedShellRef} className={styles.stackedShell} style={layout.shellPaginatedStyle}>

          <div ref={layout.stackedListRegionRef} className={styles.stackedListRegion}>

            {stackedInner}

          </div>

          {paginationFooter}

        </div>

      </Paper>

    );

  }



  const emptyStatus = (
    <DataTableEmptyState
      emptyMessage={emptyMessage}
      emptyDescription={emptyDescription}
      emptyAction={emptyAction}
      emptyVariant={emptyVariant}
      compactEmpty={compactEmpty}
    />
  );

  const tableBody = (
    <DataTableTableView
      tableRef={tableRef}
      wpTable={wpTable}
      resolvedColumns={resolvedColumns}
      rowKey={rowKey}
      dataLength={data.length}
      totalCols={totalCols}
      effectiveFixedLayout={effectiveFixedLayout}
      stickyHScroll={stickyHScroll}
      columnResizeEnabled={columnResizeEnabled}
      resizableTableStyle={resizableTableStyle}
      colGroupCols={colGroupCols}
      compactActions={compactActions}
      density={density}
      rowLeadingActions={rowLeadingActions}
      rowActions={rowActions}
      actionsHeaderColumn={
        actionsHeaderFilter
          ? {
              key: '__actions',
              label: '',
              headerAriaLabel: actionsHeaderAriaLabel ?? 'Actions',
              headerFilter: actionsHeaderFilter,
              headerFilterActive: actionsHeaderFilterActive,
              headerFilterBadge: actionsHeaderFilterBadge,
              onHeaderFilterReset: onActionsHeaderFilterReset,
              headerFilterResetAriaLabel: actionsHeaderFilterResetAriaLabel,
            }
          : undefined
      }
      selection={selection}
      pageSelectableKeys={pageSelectableKeys}
      pageSelectionState={pageSelectionState}
      onTogglePageSelection={onTogglePageSelection}
      onToggleRowSelection={onToggleRowSelection}
      onRowClick={onRowClick}
      rowProps={rowProps}
      rowTrWrapper={rowTrWrapper}
      showQuickCreateRow={showQuickCreateRow}
      quickCreateCells={quickCreateCells}
      quickCreateCollapsed={quickCreateCollapsed}
      quickCreateExpanded={quickCreateExpanded}
      mergeCellStyles={mergeCellStyles}
      resizingKey={resizingKey}
      onResizePointerDown={onResizePointerDown}
      resetColumnWidth={resetColumnWidth}
      rowHScrollDrag={rowHScrollDrag}
      rowHScrollTbodyProps={rowHScrollTbodyProps}
      suppressRowClickRef={suppressRowClickRef}
      virtualizeRows={virtualizeRowsActive}
      scrollParentRef={scrollParentRef as RefObject<HTMLElement | null>}
      emptyState={data.length === 0 ? emptyStatus : undefined}
      receivingSkeleton={receiving && data.length === 0}
      animateReceive={animateReceive}
    />
  );

  const rootProps = getDataTableRootProps({

    scroll: stickyHScroll ? 'x' : 'none',

    density,

    layout: layout.layoutMode,

    viewportFitRegion: fitViewportActive && !layout.shrinkWrapViewportBody,

    virtualScrollRegion:
      virtualizeRowsEnabled && layout.showFooter && scrollParentReady && viewportLockChain,

    serverFillRegion: layout.serverPaginated && layout.fitViewport,

    resizable: columnResizeEnabled,

    resizing: Boolean(resizingKey),

    rowScrollDrag: rowHScrollDrag,

    narrow: mobileTableScroll,

    empty: data.length === 0,

    compactEmpty: compactEmpty && data.length === 0,

    stickyActionsEnd,

    variant,

    selectionActive: hasSelectionColumn && selectionActive,

    className: cx(
      classNames?.root,
      loading && data.length > 0 && styles.loadingOverlayHost,
    ),

    tabIndex: stickyHScroll ? 0 : undefined,

    'aria-label': stickyHScroll

      ? 'Colonnes supplémentaires — faire défiler horizontalement ou faire glisser une ligne'

      : undefined,

    style:

      hasActionColumn && stickyActionsEnd

        ? ({ '--wp-datatable-actions-col-min': `${actionColumnWidthPx}px` } as CSSProperties)

        : undefined,

  });

  const refreshOverlay =
    loading && data.length > 0 ? (
      <div className={styles.loadingOverlay} aria-busy="true" aria-live="polite">
        <Loader />
      </div>
    ) : null;



  if (!layout.showFooter) {

    const embeddedBody = (

      <>

        {tableBody}

      </>

    );

    const embeddedScrollHost = selection ? (
      <div className={styles.selectionHost}>
        <div {...rootProps}>
          {embeddedBody}
          {refreshOverlay}
        </div>
        {selectionDock}
      </div>
    ) : (
      <div {...rootProps}>
        {embeddedBody}
        {refreshOverlay}
      </div>
    );

    const embeddedChrome = (
      <div className={styles.embeddedChrome} data-wp-datatable-chrome="flat">
        {scrollable ? (
          <ScrollArea ref={virtualizeRowsActive ? scrollParentRef : undefined} maxHeight={maxBodyHeight}>
            {embeddedScrollHost}
          </ScrollArea>
        ) : (
          embeddedScrollHost
        )}
      </div>
    );

    return embeddedChrome;

  }



  const shellProps = getDataTableShellProps({

    layout: layout.layoutMode,

    viewportFit: fitViewportActive,

    serverFill: layout.serverPaginated && fitViewportActive,

    stickyActionsEnd,

    className: classNames?.shell,

  });



  return (

    <Paper
      withBorder
      p="none"
      radius="md"
      shadow="none"
      style={{
        boxShadow: 'none',
        overflow: 'hidden',
        ...(fitViewportActive
          ? {
              display: 'flex',
              flexDirection: 'column',
              flex: '1 1 auto',
              minHeight: 0,
              height: '100%',
            }
          : {}),
        ...layout.shellPaperStyle,
      }}
    >

      <div
        ref={layout.paginatedShellRef}
        {...shellProps}
        style={{
          ...layout.shellPaginatedStyle,
          ...(layout.fitViewport && !fitViewportActive
            ? { height: undefined, flex: undefined, minHeight: undefined }
            : {}),
        }}
      >

        {selection ? (
          <div className={styles.selectionHost}>
            <ScrollArea
              ref={assignTableBodyRegionRef}
              orientation="both"
              className={styles.tableScrollArea}
              viewportClassName={styles.tableScrollViewport}
            >
              <div {...rootProps} className={cx(rootProps.className, styles.tableScrollInner)}>
                {tableBody}
                {refreshOverlay}
              </div>
            </ScrollArea>
            {selectionDock}
          </div>
        ) : (
          <ScrollArea
            ref={assignTableBodyRegionRef}
            orientation="both"
            className={styles.tableScrollArea}
            viewportClassName={styles.tableScrollViewport}
          >
            <div {...rootProps} className={cx(rootProps.className, styles.tableScrollInner)}>
              {tableBody}
              {refreshOverlay}
            </div>
          </ScrollArea>
        )}

        {paginationFooter}

      </div>

    </Paper>

  );

}

